import React, { useState, useEffect } from 'react';
import { showCanvas } from '../app.tsx';
import { CameraController, CameraViews } from '../../camera-controller.ts';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface SummaryImageProps {
	cameraController: CameraController;
	setIsDisabled: (isDisabled: boolean) => void;
	className?: string;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function SummaryImage(props: SummaryImageProps) {

	// states.
	const [summaryImage, setSummaryImage] = useState<string | null>(null);

	// handlers.

	const handleDownloadItem = (downloadUrl: string, fileName: string) => {

		// imitate clicking an element to trigger download.
		const link = document.createElement('a');
		link.href = downloadUrl;
		link.download = fileName;
		link.click();
	}

	const convertCanvasToPng = async (canvas: HTMLCanvasElement): Promise<string> => {

		// return a promise so the function can be awaited.
		return new Promise<string>((resolve) => {

			// resize canvas to make larger images.
			const highResCanvas = handleScaleCanvas(canvas, 2);

			// delays the screenshot to ensure the canvas is fully rendered. 
			requestAnimationFrame(() => {
				const image = highResCanvas.toDataURL('image/png');

				// resolve promise to allow next screenshot to be taken.
				resolve(image);
				return image;
			});
		});
	};

	const handleTakeScreenshot = async (canvas: HTMLCanvasElement, imageName: string): Promise<void> => {

		// convert canvas to png and download.
		const image = await convertCanvasToPng(canvas);
		handleDownloadItem(image, imageName);
	};

	const handleDownloadImagePack = async () => {
		const cameraViews = [
			CameraViews.ModelView,
			CameraViews.LeatherView,
			CameraViews.HoodView,
			CameraViews.CowlView
		];

		props.setIsDisabled(true);

		// add canvas to the DOM.
		showCanvas(true);

		const canvas = document.querySelector('canvas');

		// handle if there is no canvas.
		if (!canvas) {
			console.error('Canvas not found!');
			props.setIsDisabled(false);
			return;
		}

		// don't actually show canvas to the user.
		canvas.style.opacity = '0';

		// brief time out to give canvas time to render.
		await new Promise((resolve) => setTimeout(resolve, 500));

		// create an image at each camera view.
		for (const [index, cameraView] of cameraViews.entries()) {
			props.cameraController.changeCameraView(cameraView, () => null, true);

			// wait for the engine to render the new view before capturing, otherwise the canvas still holds the previous view's frame.
			await new Promise((resolve) => requestAnimationFrame(() => resolve(true)));

			await handleTakeScreenshot(canvas, `supersport-${index + 1}.png`);
		}

		// reset styles and visibility on canvas.
		showCanvas(false);
		canvas.style.opacity = '1';
		props.setIsDisabled(false);
	};

	const handleShare = async () => {
		const url = location.pathname;

		props.setIsDisabled(true);

		// add canvas to the DOM.
		showCanvas(true);

		const canvas = document.querySelector('canvas');

		// handle if there is no canvas.
		if (!canvas) {
			console.error('Canvas not found!');
			props.setIsDisabled(false);
			return;
		}

		// don't actually show canvas to the user.
		canvas.style.opacity = '0';

		// give canvas time to render
		await new Promise((resolve) => setTimeout(resolve, 300));

		// update camera view.
		props.cameraController.changeCameraView(CameraViews.ModelView, () => null, true);

		await new Promise((resolve) => requestAnimationFrame(() => resolve(true)));

		// generate image from canvas.
		const highResCanvas = handleScaleCanvas(canvas, 2);
		const imgUrl = highResCanvas.toDataURL('image/jpeg');

		// convert to blob.
		const response = await fetch(imgUrl);
		const imgBlob = await response.blob();

		// convert to image file.
		const imgFile = new File([imgBlob], 'supersport.jpg', { type: imgBlob.type });
		const data = { files: [imgFile] };

		// revert canvas styles.
		showCanvas(false);
		canvas.style.opacity = '1';
		props.setIsDisabled(false);

		// share the image.
		try {
			if (window.navigator.canShare && window.navigator.canShare(data)) {
				await navigator.share(data);
			} else {
				await navigator.clipboard.writeText(url);
				alert('Image link copied to clipboard!');
			}
		} catch (err) {
			console.error('Share failed:', err);
			alert('Could not share the image.');
		}
	}

	useEffect(() => {

		// generate the summary image once on mount.
		const generateSummaryImage = async () => {

			// reset camera fov before taking images.
			props.cameraController.resetCameraFov();

			// add canvas to the DOM.
			showCanvas(true);

			const canvas = document.querySelector('canvas');

			// handle if there is no canvas.
			if (!canvas) {
				console.error('Canvas not found!');
				return null;
			}

			// don't actually show canvas to the user.
			canvas.style.opacity = '0';

			// give canvas time to render.
			await new Promise((resolve) => setTimeout(resolve, 300));

			props.cameraController.changeCameraView(CameraViews.PdfModelView, () => null, true);

			// give canvas time to render the new view.
			await new Promise((resolve) => requestAnimationFrame(() => resolve({})));

			const highResCanvas = handleScaleCanvas(canvas, 2);
			const imgUrl = highResCanvas.toDataURL('image/jpeg');

			// revert canvas styles.
			showCanvas(false);
			canvas.style.opacity = '1';
			props.cameraController.changeCameraView(CameraViews.ModelView, () => null, true);

			// return the generated image so it can be set in state.
			return imgUrl;
		};

		// set the image in the promise callback, not the effect body, to avoid a synchronous setState.
		generateSummaryImage()
			.then(setSummaryImage)
			.catch((err: unknown) => console.error('failed to generate summary image: ', err));
	}, [props.cameraController]);

	return (
		<div className={props.className}>
			{summaryImage ? (
				<div style={{ position: 'relative' }}>
					<img src={summaryImage} width="100%" height="auto" />
					<div className="share-btn-container">
						<button onClick={handleDownloadImagePack}>
							<img src="assets/images/icons/download-3.svg" width="40" height="40" />
						</button>
						<button onClick={handleShare}>
							<img src="assets/images/icons/share-3.svg" width="40" height="40" />
						</button>
					</div>
				</div>
			) : (
				<p>Loading image...</p>
			)}
		</div>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// increases canvas width and height by a scale factor to create larger images.
const handleScaleCanvas = (canvas: HTMLCanvasElement, scale: number): HTMLCanvasElement => {
	const newCanvas = document.createElement('canvas');
	newCanvas.width = canvas.width * scale;
	newCanvas.height = canvas.height * scale;

	const ctx = newCanvas.getContext('2d');
	if (!ctx) return canvas;

	ctx.drawImage(canvas, 0, 0, newCanvas.width, newCanvas.height);
	return newCanvas;
};
